import PropTypes from 'prop-types';

import { trans, useSoyuzFlag } from '@spotahome/soyuz/client';
import {
  Tooltip,
  InfoIcon,
  cityToggleConfig,
  InfoBanner,
  CreditCardIcon
} from '@spotahome/ui-library';
import {
  formatPrice,
  getCurrencySymbolForISOCode
} from '@spotahome/soyuz-currency';

import { ListingShape } from '@spotahome/marketplace-common/src/shapes/listing';
import ListRow from '@spotahome/marketplace-common/src/components/ListRow';
import ListingContent from '@spotahome/marketplace-common/src/components/ListingContent';
import {
  useDisplayListingPricing,
  useDisplayPriceIndex,
  usePricingContext
} from '@spotahome/marketplace-common/src/context/PricingContext';

import CopyWithInterpolatedLink from '@spotahome/ui-library/src/tenant/components/CopyWithInterpolatedLink';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { getSecurityDepositValue } from '../../utils/getSecurityDepositValue';

import ListingTitle from '../ListingTitle';

import { ListingSectionObservable } from '../ListingSection';

import SuggestPrice from '../SuggestPrice';

import ListingBills from './ListingBills';
import CancellationPolicyModal from './CancellationPolicyModal';
import ServicesAndExpenses from './ServicesAndExpenses';

import './ListingPricing.scss';

const ListingPricing = ({
  bills,
  contractType,
  currencyLabel,
  cityId,
  roomDescription = '',
  m2 = undefined,
  listingType,
  hasDates,
  titleKey = '',
  landlordPolicies = '',
  servicesAndExpenses = null,
  isLogged,
  listingId,
  listing,
  moveIn,
  moveOut,
  deviceType,
  hasHiddenFees = false,
  hasRoomDescriptionPricing = false
}) => {
  const showDescription = roomDescription && hasRoomDescriptionPricing;

  const pricingContext = usePricingContext();
  const { price } = useDisplayListingPricing();
  const { priceIndex } = useDisplayPriceIndex(m2);
  const priceIndexFlag = useSoyuzFlag('ShowPriceIndex');
  const {
    detailedAdminFee: { depositValue, depositType, feeValue: adminFeeValue },
    pricingDetails: { pricing },
    priceRange: { fixedPrice, seasonalPrice },
    isLoaded: isPricingLoaded
  } = pricingContext;

  const currencySymbol = getCurrencySymbolForISOCode(currencyLabel);

  const securityDeposit = getSecurityDepositValue(
    currencyLabel,
    cityId,
    pricing && pricing.contractDurationType,
    depositValue,
    depositType,
    hasDates
  );
  const showPriceIndexValue = !listingType.includes('room') && !!priceIndex;
  const showPriceIndexTooltip = cityToggleConfig(cityId).showPriceIndexTooltip;
  const showPriceIndexSection =
    priceIndexFlag.isOn() && (showPriceIndexTooltip || showPriceIndexValue);

  const adminFee =
    hasDates && isPricingLoaded
      ? formatPrice(adminFeeValue, currencyLabel)
      : trans('listing.pricing.no-dates');

  const offerPrice = fixedPrice || seasonalPrice?.max;

  const sendLandlordPoliciesEvent = () => {
    SoyuzAnalytics.sendGA4Event('open', {
      section: 'landlord-policies-hidden-fees'
    });
  };

  return (
    <ListingSectionObservable section="pricing">
      <div className="listing-pricing" data-test="listing-pricing">
        {titleKey && (
          <ListingTitle role="secondary">{trans(titleKey)}</ListingTitle>
        )}
        <div className="listing-pricing-structured-container">
          <div className="listing-pricing-structured">
            <div className="listing-pricing-structured__per-month">
              <ListingTitle role="tertiary">
                {trans('listing.pricing.title.price-per-month')}
              </ListingTitle>
              {showDescription && (
                <ListRow
                  renderLeftBlock={() => (
                    <p>{trans('listing.pricing.room-description')}</p>
                  )}
                  renderRightBlock={() => (
                    <div className="listing-pricing-structured__room-description">
                      <span
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: roomDescription }}
                      />
                    </div>
                  )}
                />
              )}
              <ListRow
                renderLeftBlock={() => (
                  <p>{trans('listing.pricing.monthly-rent')}</p>
                )}
                renderRightBlock={() => (
                  <p className="listing-pricing-structured__rent">{price}</p>
                )}
              />
              <SuggestPrice
                listingId={listingId}
                isLogged={isLogged}
                currencyIsoCode={currencyLabel}
                offerPrice={offerPrice}
                deviceType={deviceType}
                hasDates={
                  moveIn && (moveOut || listing.allowsPermanentContract)
                }
              />
              <div className="listing-pricing-structured__bills">
                <ListingBills
                  bills={bills}
                  listingType={listingType}
                  currency={currencySymbol}
                />
              </div>
            </div>
            <div className="listing-pricing-structured__move-in">
              <ListingTitle role="tertiary">
                {trans('listing.pricing.title.pay-move-in')}
              </ListingTitle>
              <ListRow
                withBorder
                renderLeftBlock={() => {
                  return (
                    <span>{trans('listing.pricing.security-deposit')} </span>
                  );
                }}
                renderRightBlock={() => (
                  <p
                    className="listing-pricing-structured__security-deposit"
                    data-test="listing-security-deposit"
                  >
                    {securityDeposit}
                  </p>
                )}
              />
              {!!adminFeeValue && (
                <ListRow
                  renderLeftBlock={() => (
                    <span>
                      {trans('listing.pricing.admin-fee')}{' '}
                      <Tooltip
                        className="listing-pricing__tooltip"
                        color="blue-cerulean"
                        tooltip={trans('listing.fees.admin-fee.button-text')}
                        placement="top"
                        trigger={['click', 'hover']}
                      >
                        <InfoIcon size={12} />
                      </Tooltip>
                    </span>
                  )}
                  renderRightBlock={() => (
                    <b className="listing-pricing-structured__admin-fee">
                      {adminFee}
                    </b>
                  )}
                />
              )}
            </div>
          </div>

          <div className="listing-pricing-structured">
            <div className="listing-pricing-structured__services-expenses">
              <ServicesAndExpenses
                currencyLabel={currencyLabel}
                periodicCleaning={servicesAndExpenses?.periodicCleaning}
              />
            </div>
            <div className="listing-pricing-structured__details">
              <ListingTitle role="tertiary">
                {trans('listing.pricing.title.details')}
              </ListingTitle>
              <ListRow
                renderLeftBlock={() => (
                  <span>
                    {trans('listing.pricing.details.type-of-contract')}{' '}
                    <Tooltip
                      className="listing-pricing__tooltip"
                      color="blue-cerulean"
                      tooltip={trans(
                        `contract_type.descripition.${contractType}`
                      )}
                      placement="top"
                      trigger={['click', 'hover']}
                    >
                      <InfoIcon size={12} />
                    </Tooltip>
                  </span>
                )}
                renderRightBlock={() => (
                  <div className="listing-pricing-structured__details-type-contract">
                    {trans(`contract_type.label.${contractType}`)}
                  </div>
                )}
              />
              <ListRow
                withBorder={showPriceIndexSection}
                renderLeftBlock={() => (
                  <p>{trans('listing.pricing.cancellation-policy.title')}</p>
                )}
                renderRightBlock={() => <CancellationPolicyModal />}
              />
              {showPriceIndexSection && (
                <ListRow
                  withBorder={false}
                  renderLeftBlock={() => (
                    <span>
                      {trans('listing.pricing.price-index.title')}{' '}
                      {showPriceIndexTooltip && (
                        <Tooltip
                          className="listing-pricing__tooltip"
                          color="blue-cerulean"
                          tooltip={trans('listing.pricing.price-index.tooltip')}
                          placement="top"
                          trigger={['click', 'hover']}
                        >
                          <InfoIcon size={12} />
                        </Tooltip>
                      )}
                    </span>
                  )}
                  renderRightBlock={() =>
                    showPriceIndexValue && (
                      <p className="listing-pricing__square-meter">
                        {priceIndex}
                      </p>
                    )
                  }
                />
              )}
              <div>
                {hasHiddenFees && (
                  <InfoBanner
                    theme="suggestion-light"
                    closable={false}
                    icon={<CreditCardIcon />}
                    className="listing-pricing__hidden-fees"
                    content={
                      <CopyWithInterpolatedLink
                        mainCopy="listing.hidden-fees.text"
                        linkCopy="listing.hidden-fees.link"
                        link="#landlordRules"
                        onClick={sendLandlordPoliciesEvent}
                      />
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {landlordPolicies && (
          <div className="listing-pricing-structured__landlord-policies">
            <ListingTitle role="tertiary">
              {trans('listing.pricing.title.landlord-policies')}
            </ListingTitle>
            <ListingContent content={landlordPolicies} />
          </div>
        )}
      </div>
    </ListingSectionObservable>
  );
};

ListingPricing.propTypes = {
  bills: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  cityId: PropTypes.string.isRequired,
  contractType: PropTypes.string.isRequired,
  currencyLabel: PropTypes.string.isRequired,
  pricing: PropTypes.shape({
    contractDurationType: PropTypes.string
  }).isRequired,
  titleKey: PropTypes.string,
  roomDescription: PropTypes.string,
  m2: PropTypes.number,
  listingType: PropTypes.string.isRequired,
  hasDates: PropTypes.bool.isRequired,
  landlordPolicies: PropTypes.string,
  servicesAndExpenses: PropTypes.shape({
    periodicCleaning: PropTypes.shape({
      value: PropTypes.string.isRequired,
      additionalInfo: PropTypes.shape({
        fee: PropTypes.number,
        frequency: PropTypes.string
      })
    })
  }),
  isLogged: PropTypes.bool.isRequired,
  listingId: PropTypes.number.isRequired,
  listing: PropTypes.shape(ListingShape).isRequired,
  moveIn: PropTypes.string.isRequired,
  moveOut: PropTypes.string.isRequired,
  deviceType: PropTypes.string.isRequired,
  hasHiddenFees: PropTypes.bool,
  hasRoomDescriptionPricing: PropTypes.bool
};

export default ListingPricing;
